{
    "title": "Integrations",
    "subtitle": "Everything this platform talks to, and whether it is working.",
    "add": "Add connector",
    "category": "Category",
    "connector": "Connector",
    "configure": "Configure",
    "test": "Test",
    "retry": "Retry",
    "enabled": "Enabled",
    "disabled": "Disabled",
    "enable": "Enabled",
    "offshore": "Data leaves Oman",
    "offshore_help": "Tick this if traffic to this system crosses the border. It is a compliance decision, and it belongs beside the connector rather than in a document.",
    "driver_missing": "Driver unavailable",
    "key": "Key",
    "name_en": "Name (English)",
    "name_ar": "Name (Arabic)",
    "timeout": "Timeout (seconds)",
    "rate_limit": "Rate limit (per minute)",
    "rate_limit_help": "Leave blank for no limit. A system that falls over at twenty requests a second is a production incident we caused.",
    "max_attempts": "Delivery attempts",
    "connector_settings": "Connector settings",
    "operation": "Operation",
    "attempts": "Attempts",
    "error": "Error",
    "status": "Status",
    "when": "When",
    "calls_24h": "Calls (24h)",
    "success_rate": "Success rate",
    "mean_ms": "Mean",
    "last_checked": "Last checked",
    "recent_failures": "Recent failures",
    "no_failures": "Nothing has failed.",
    "logs_permission": "You do not have permission to read integration logs.",
    "categories": {
        "core_banking": "Core systems",
        "messaging": "Messaging",
        "identity": "Identity",
        "analytics": "Analytics",
        "telephony": "Telephony",
        "storage": "Storage",
        "government": "Government and regulatory"
    },
    "drivers": {
        "core_banking": "Core banking",
        "loan_origination": "Loan origination",
        "sms": "SMS gateway",
        "email": "Email",
        "whatsapp": "WhatsApp Business",
        "telephony_bridge": "Telephony",
        "directory": "Directory (LDAP / SAML / OIDC)",
        "data_warehouse": "Data warehouse",
        "object_storage": "Object storage",
        "regulatory": "Central Bank returns"
    },
    "channels": {
        "sms": "SMS",
        "email": "Email",
        "whatsapp": "WhatsApp"
    },
    "health": {
        "healthy": "Working",
        "degraded": "Failing intermittently",
        "unhealthy": "Not working",
        "misconfigured": "Needs configuration",
        "unknown": "Not yet checked"
    },
    "health_column": "Health",
    "health_page": {
        "title": "Integration health",
        "subtitle": "What is working, what is stuck, and what nobody has been told.",
        "connectors": "Connectors",
        "dead_letters_one": "One message could not be delivered and has stopped being retried. Somebody was not told something.",
        "dead_letters_other": "{{count}} messages could not be delivered and have stopped being retried. That many people were not told something."
    },
    "outbox": {
        "title": "Outbox",
        "empty": "Nothing here.",
        "status": {
            "pending": "Waiting",
            "dispatching": "Sending",
            "delivered": "Delivered",
            "dead_letter": "Undeliverable"
        }
    },
    "form": {
        "no_fields": "This connector needs no configuration.",
        "secret_set": "set",
        "secret_unchanged": "Leave blank to keep the current value",
        "secret_blank_keeps": "Leave blank to keep whatever is stored. Nothing can display the current value.",
        "secret_rotated_at": "Last changed {{date}}. Leave blank to keep it."
    },
    "webhooks": {
        "title": "Webhooks",
        "subtitle": "What other systems send us, and what happened to it.",
        "new": "New endpoint",
        "slug": "Path",
        "url": "URL to give the sending system",
        "signing_secret": "Signing secret",
        "secret_required_help": "Required. An endpoint with no secret refuses every request — which is correct, but looks broken rather than unconfigured.",
        "timestamp_header": "Timestamp header",
        "timestamp_help": "Optional. Without it a valid signature never expires, and anybody who saw one request can replay it.",
        "tolerance": "Tolerance (seconds)",
        "algorithm": "Algorithm",
        "header": "Signature header",
        "received": "Received",
        "rotate": "Generate new secret",
        "secret_once": "This is the only time this secret will be shown. Copy it into the sending system now.",
        "no_secret": "No signing secret",
        "deliveries": "Deliveries",
        "no_deliveries": "Nothing has arrived yet.",
        "endpoint": "Endpoint",
        "event": "Event",
        "replay": "Replay",
        "statuses": {
            "received": "Accepted",
            "processed": "Processed",
            "rejected": "Rejected"
        },
        "rejections": {
            "signature_mismatch": "Bad signature",
            "signature_missing": "No signature",
            "no_signing_secret": "No secret configured",
            "timestamp_outside_tolerance": "Timestamp expired",
            "ip_not_allowed": "IP not allowed",
            "endpoint_inactive": "Endpoint inactive"
        }
    },
    "keys": {
        "title": "API keys",
        "subtitle": "Credentials for the mobile application and other machine callers.",
        "new": "New key",
        "create": "Create key",
        "name": "Name",
        "prefix": "Prefix",
        "scopes": "Permitted scopes",
        "all_scopes": "All scopes",
        "requests": "Requests",
        "last_used": "Last used",
        "never_used": "Never used",
        "expires": "Expires",
        "expiry_help": "A key with no expiry outlives the project that needed it.",
        "revoke": "Revoke",
        "revoked": "Revoked",
        "empty": "No keys yet.",
        "shown_once": "This is the only time the key for “{{name}}” will be shown. Copy it now."
    },
    "simulators": {
        "title": "Simulators",
        "subtitle": "How the platform behaves with no external system configured — which is how it will behave on the day one goes down.",
        "messages": "Message log",
        "inbound": "Inbound mailbox",
        "directory": "Directory",
        "send": "Send a test message",
        "send_help": "This goes through the outbox, the dispatcher and the real connector — not straight into the log. What it proves is the whole path.",
        "channel": "Channel",
        "recipient": "To",
        "subject": "Subject",
        "body": "Message",
        "log": "What was sent",
        "no_messages": "Nothing has been sent yet.",
        "segments_one": "1 part, {{encoding}}",
        "segments_other": "{{count}} parts, {{encoding}} — an Arabic message holds 70 characters a part, not 160, and a gateway bills per part.",
        "deliver": "Deliver to the mailbox",
        "from": "From",
        "from_help": "Use the email address of a seeded customer to see a complaint created, or any other address to see it refused.",
        "subject_help": "Include a complaint reference to thread the message onto the existing complaint instead of creating a new one.",
        "outcome": "What happened",
        "no_outcome": "Deliver a message to see what the platform does with it.",
        "actions": {
            "created": "Complaint created",
            "appended": "Added to the existing complaint",
            "unmatched_sender": "No matching customer",
            "ignored_empty": "Ignored — no content"
        },
        "unmatched_explained": "No customer holds that email address, so nothing was created. Creating a customer from a sender address would fill the master record with ghosts from mailing lists and out-of-office replies.",
        "matched_explained": "The sender was matched to the customer master, so the complaint sits on that person’s one timeline alongside their calls, visits and surveys.",
        "directory_users": "Seeded directory",
        "shared_password": "Every simulated account uses the password “{{password}}”.",
        "sign_in": "Sign in",
        "username": "Username",
        "password": "Password",
        "groups": "Directory groups",
        "roles": "Roles this grants",
        "admitted": "This person would be admitted with the roles above.",
        "not_admitted": "The credentials are correct, but no group maps to a role — so this person is authenticated and still refused."
    }
}
